import { AppointmentModel, AppointmentService } from "../fhir/AppointmentService";
import { SlotService } from "../fhir/SlotService";
import { referenceKey } from "../fhir/fhir.utils";
import {
  CalendarEvent,
  CalendarRange,
  ScheduleSummary,
} from "./Scheduling.types";

function toCalendarEvent(
  appointment: AppointmentModel,
  sourceIds: string[],
): CalendarEvent {
  return {
    id: appointment.id,
    title: appointment.title,
    description: appointment.description,
    start: appointment.start,
    end: appointment.end,
    status: appointment.status,
    scheduleIds: sourceIds,
    participantDisplays: appointment.participantDisplays,
    locationDisplay: appointment.locationDisplay,
    comment: appointment.comment,
    patientInstruction: appointment.patientInstruction,
  };
}

function mergeEvent(
  events: Map<string, CalendarEvent>,
  appointment: AppointmentModel,
  sourceIds: string[],
) {
  if (sourceIds.length === 0) {
    return;
  }

  const existing = events.get(appointment.id);
  if (!existing) {
    events.set(appointment.id, toCalendarEvent(appointment, sourceIds));
    return;
  }

  existing.scheduleIds = Array.from(
    new Set([...existing.scheduleIds, ...sourceIds]),
  );
  existing.participantDisplays = Array.from(
    new Set([...existing.participantDisplays, ...appointment.participantDisplays]),
  );
}

export class CalendarService {
  constructor(
    private readonly slotService: SlotService,
    private readonly appointmentService: AppointmentService,
  ) {}

  async load(
    selectedSourceIds: string[],
    allSources: ScheduleSummary[],
    range: CalendarRange,
  ): Promise<CalendarEvent[]> {
    if (selectedSourceIds.length === 0) {
      return [];
    }

    const selectedIds = new Set(selectedSourceIds);
    const realSources = allSources.filter((source) => source.kind === "schedule");
    const selectedRealSources = realSources.filter((source) =>
      selectedIds.has(source.id),
    );
    const selectedVirtualSources = allSources.filter(
      (source) => source.kind === "unlinked" && selectedIds.has(source.id),
    );
    const unlinkedActorReferences = new Set(
      selectedVirtualSources.map((source) => source.actorReference),
    );

    const schedulesNeededForClassification = realSources.filter((source) =>
      unlinkedActorReferences.has(source.actorReference),
    );
    const scheduleIdsToLoad = Array.from(
      new Set([
        ...selectedRealSources.map((source) => source.id),
        ...schedulesNeededForClassification.map((source) => source.id),
      ]),
    );

    const slots = await this.slotService.searchBySchedules(
      scheduleIdsToLoad,
      range.start,
      range.end,
    );
    const scheduleIdBySlot = new Map(
      slots.map((slot) => [`Slot/${slot.id}`, slot.scheduleId]),
    );
    const actorReferenceByScheduleId = new Map(
      realSources.map((source) => [source.id, source.actorReference]),
    );

    const [slotAppointments, actorAppointments] = await Promise.all([
      this.appointmentService.searchBySlots(slots.map((slot) => slot.id)),
      this.appointmentService.searchByActors(
        Array.from(unlinkedActorReferences),
        range.start,
        range.end,
      ),
    ]);

    const events = new Map<string, CalendarEvent>();

    slotAppointments.forEach((appointment) => {
      const sourceIds = Array.from(
        new Set(
          appointment.slotReferences
            .map((slotReference) =>
              scheduleIdBySlot.get(referenceKey(slotReference) ?? slotReference),
            )
            .filter(
              (scheduleId): scheduleId is string =>
                Boolean(scheduleId && selectedIds.has(scheduleId)),
            ),
        ),
      );
      mergeEvent(events, appointment, sourceIds);
    });

    selectedVirtualSources.forEach((virtualSource) => {
      actorAppointments
        .filter((appointment) =>
          appointment.participantReferences.includes(virtualSource.actorReference),
        )
        .forEach((appointment) => {
          const linkedToActorSchedule = appointment.slotReferences.some(
            (slotReference) => {
              const scheduleId = scheduleIdBySlot.get(
                referenceKey(slotReference) ?? slotReference,
              );
              return (
                scheduleId !== undefined &&
                actorReferenceByScheduleId.get(scheduleId) ===
                  virtualSource.actorReference
              );
            },
          );

          if (!linkedToActorSchedule) {
            mergeEvent(events, appointment, [virtualSource.id]);
          }
        });
    });

    return Array.from(events.values()).sort(
      (left, right) =>
        new Date(left.start).getTime() - new Date(right.start).getTime(),
    );
  }
}
